import { Component, inject } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { DataDisplayComponent, ButtonComponent, DataContainerComponent } from 'htx-ui-library';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap
} from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { RepresentativeChangeService } from '../../../../../services/representative-change/representative-change.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import {
  OrganizationDetails,
  AdmissionState,
  CommentType
} from '../../../../../services/organizations/organization.model';
import { RepresentativeChangeRequest, RepresentativeChangeComment } from '../../../../../services/representative-change/representative-change.model';
import {
  CommentsComponent,
  Comment
} from '../../../../../components/organisms/comments/comments.component';
import { ProtectedContentComponent } from '../../../../../components/organisms/protected-content/protected-content.component';
import { AdmissionDecisionComponent } from '../../../../../components/organisms/admission-decision/admission-decision.component';
import { NavigationService } from '@services/navigation.service';
import {
  PdfViewerDialogComponent,
  PdfViewerDialogData
} from '../../../../organisms/pdf-viewer/pdf-viewer.component';

@Component({
  selector: 'app-representative-change-admission-page',
  imports: [
    AsyncPipe,
    CommonModule,
    DataDisplayComponent,
    DataContainerComponent,
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    ProtectedContentComponent,
    ButtonComponent,
    AdmissionDecisionComponent,
    CommentsComponent
  ],
  templateUrl: './representative-change-admission-page.component.html',
  styleUrl: './representative-change-admission-page.component.scss'
})
export class RepresentativeChangeAdmissionPageComponent {
  private fb = inject(FormBuilder);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private organizationService = inject(OrganizationService);
  private representativeChangeService = inject(RepresentativeChangeService);
  private notificationService = inject(NotificationService);
  private navigationService = inject(NavigationService);
  private dialog = inject(MatDialog);

  requiredRoles = this.navigationService.getAccessRoles('admin/requests');

  approvalForm = this.fb.group({
    reasoning: ['', Validators.required],
    comment: ['', Validators.required]
  });

  id$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('id')!));

  private dataRefresh$ = new BehaviorSubject<void>(undefined);

  changeRequest$: Observable<RepresentativeChangeRequest> = combineLatest([
    this.id$,
    this.dataRefresh$
  ]).pipe(
    switchMap(([id]) => this.representativeChangeService.getDetailsById(id))
  );

  organizationData$: Observable<OrganizationDetails> = this.changeRequest$.pipe(
    switchMap((request) => this.organizationService.getDetailsById(request.organizationId))
  );

  admissionState$ = this.changeRequest$.pipe(map((data) => data.admissionState));

  private commentsRefresh$ = new BehaviorSubject<void>(undefined);

  comments$: Observable<RepresentativeChangeComment[]> = combineLatest([
    this.id$,
    this.commentsRefresh$
  ]).pipe(
    switchMap(([id]) => this.representativeChangeService.getComments(id))
  );

  existingReasoning$ = combineLatest([this.admissionState$, this.comments$]).pipe(
    map(([admissionState, comments]) => {
      if (admissionState === AdmissionState.APPROVED) {
        const approvalComment = comments.find((c) => c.type === CommentType.APPROVAL);
        return approvalComment?.text || 'Keine Begründung angegeben';
      } else if (admissionState === AdmissionState.REJECTED) {
        const rejectionComment = comments.find((c) => c.type === CommentType.REJECTION);
        return rejectionComment?.text || 'Keine Begründung angegeben';
      }
      return '';
    })
  );

  commentsForBox$: Observable<Comment[]> = this.comments$.pipe(
    map((comments) =>
      comments.map((c) => ({
        id: c.id,
        author: c.author,
        text: c.text,
        date: c.date,
        type: c.type || CommentType.COMMENT
      }))
    )
  );

  changeDetails$ = this.changeRequest$.pipe(
    map((request) => {
      return [
        {
          label: this.translocoService.translate('representativeChange.detail.oldRepresentative'),
          value: [request.oldRepresentativeName]
        },
        {
          label: this.translocoService.translate('representativeChange.detail.newRepresentative'),
          value: [request.newRepresentativeName]
        },
        {
          label: this.translocoService.translate('representativeChange.detail.reasoning'),
          value: [request.reasoning || '-']
        },
        {
          label: this.translocoService.translate('representativeChange.detail.requestDate'),
          value: [
            request.createdDate
              ? new Date(request.createdDate).toLocaleDateString('de-DE')
              : '-'
          ]
        }
      ];
    })
  );

  oldRepresentativeDetails$ = this.changeRequest$.pipe(
    map((request) => this.mapMemberDetails(request.oldRepresentativeDetails))
  );

  newRepresentativeDetails$ = this.changeRequest$.pipe(
    map((request) => this.mapMemberDetails(request.newRepresentativeDetails))
  );

  private mapMemberDetails(member: RepresentativeChangeRequest['oldRepresentativeDetails']) {
    return [
      {
        label: this.translocoService.translate('approval.summary.name.label'),
        value: [
          [member.title || '', member.firstName, member.lastName]
            .filter(Boolean)
            .join(' ')
        ]
      },
      {
        label: this.translocoService.translate('approval.summary.contactData.email.label'),
        value: [member.email]
      },
      {
        label: this.translocoService.translate('approval.summary.contactData.phoneNumber.label'),
        value: [member.phoneNumber || '-']
      },
      {
        label: this.translocoService.translate('approval.summary.position.label'),
        value: [member.position || '-']
      }
    ];
  }

  organisationDetails$ = this.organizationData$.pipe(
    map((dto: OrganizationDetails) => {
      return [
        {
          label: this.translocoService.translate('approval.summary.legalName.label'),
          value: [dto.legalName + ' ' + dto.legalForm]
        },
        {
          label: this.translocoService.translate('approval.summary.foundingDate.label'),
          value: [dto.foundingDate ? new Date(dto.foundingDate).toLocaleDateString() : '']
        },
        {
          label: this.translocoService.translate('approval.summary.address.label'),
          value: [
            `${dto.organisationAddress.street} ${dto.organisationAddress.houseNumber}`,
            `${dto.organisationAddress.postalCode} ${dto.organisationAddress.city}`
          ]
        },
        {
          label: this.translocoService.translate('approval.summary.website.label'),
          value: [dto.website]
        }
      ];
    })
  );

  backToList() {
    void this.router.navigate(['admin', 'requests']);
  }

  viewDocument(): void {
    this.id$
      .pipe(
        take(1),
        switchMap((id) => this.representativeChangeService.getDocument(id))
      )
      .subscribe({
        next: (blob: Blob) => {
          const reader = new FileReader();
          reader.onload = () => {
            const arrayBuffer = reader.result as ArrayBuffer;
            this.dialog.open(PdfViewerDialogComponent, {
              data: { pdfData: arrayBuffer } as PdfViewerDialogData,
              width: '80vw',
              maxWidth: '95vw',
              height: '90vh'
            });
          };
          reader.readAsArrayBuffer(blob);
        },
        error: (err) => {
          console.error('Error loading document:', err);
          this.notificationService.showError('error.failed');
        }
      });
  }

  reject() {
    const reasoning = this.approvalForm.value.reasoning!;
    this.id$
      .pipe(
        switchMap((id) => this.representativeChangeService.reject(id, reasoning)),
        tap(() => {
          this.dataRefresh$.next();
          this.commentsRefresh$.next();
        }),
        catchError((error) => {
          console.error('Error rejecting request:', error);
          this.notificationService.showError('error.failed');
          return of(null);
        })
      )
      .subscribe();
  }

  approve() {
    const reasoning = this.approvalForm.value.reasoning!;
    this.id$
      .pipe(
        switchMap((id) => this.representativeChangeService.approve(id, reasoning)),
        tap(() => {
          this.dataRefresh$.next();
          this.commentsRefresh$.next();
        }),
        catchError((error) => {
          console.error('Error approving request:', error);
          this.notificationService.showError('error.failed');
          return of(null);
        })
      )
      .subscribe();
  }

  onCommentAdded(text: string) {
    this.id$
      .pipe(
        switchMap((id) => this.representativeChangeService.addComment(id, { text: text })),
        tap(() => this.commentsRefresh$.next()),
        catchError((error) => {
          console.error('Error adding comment:', error);
          this.notificationService.showError('error.failed');
          return of(null);
        })
      )
      .subscribe();
  }
}
